import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { isApiError, isTenantReason, } from '@agnivo/api-client';
/** Skip revalidation while a cached value is younger than this (per switch → no
 *  network, no blip). Older → background revalidate (still no blip; value shown). */
const FRESH_MS = 30_000;
const store = new Map();
/** Synchronously read a cached value (undefined = never loaded). */
export function getCached(key) {
    return store.get(key)?.value;
}
/**
 * Load a resource, dedup-ing concurrent callers and skipping refetch while the
 * cached value is still fresh. Returns the (possibly cached) value.
 */
export function loadResource(key, fetch) {
    const e = store.get(key);
    if (e?.promise)
        return e.promise; // in-flight → share it
    if (e && e.value !== undefined && Date.now() - e.fetchedAt < FRESH_MS) {
        return Promise.resolve(e.value); // fresh → no refetch
    }
    const entry = { value: e?.value, fetchedAt: e?.fetchedAt ?? 0, promise: undefined };
    store.set(key, entry);
    entry.promise = fetch()
        .then((v) => {
        entry.value = v;
        entry.fetchedAt = Date.now();
        entry.promise = undefined;
        return v;
    })
        .catch((err) => {
        entry.promise = undefined; // allow retry; keep any prior value
        throw err;
    });
    return entry.promise;
}
/** Fire-and-forget warm (launch prefetch of adjacent tabs). Errors are swallowed. */
export function prefetchResource(key, fetch) {
    void loadResource(key, fetch).catch(() => { });
}
/** Drop a cached entry so the next read refetches (e.g. after a mutation). */
export function invalidateResource(key) {
    store.delete(key);
}
/** Clear everything (e.g. on logout / user switch). */
export function clearResourceCache() {
    store.clear();
}
/**
 * How long a cold-load failure waits before the automatic single retry.
 *
 * Sized for the failure class the copy is honest about: a transient 503, a
 * network blip, a brief OIDC token refresh. Anything a 5-second wait might
 * recover is a retry; anything it cannot recover is a manual reload button.
 *
 * Bounded — the retry fires once, not on an interval. A retry loop would
 * burn battery on persistent failures and would not be honest to describe
 * as "we'll try again automatically".
 */
export const COLD_RETRY_MS = 5_000;
/** The production scheduler: a real `setTimeout`, cancelled via `clearTimeout`. */
export const realScheduleRetry = (fn, ms) => {
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
};
/**
 * The seam that makes the auto-retry testable in bounded time.
 *
 * WHY A CONTEXT AND NOT A HOOK PARAMETER. The auto-retry's observable behaviour
 * is a SCREEN behaviour — "the manual reload button appears only after the retry
 * exhausts" — so the tests that matter render `AgentsScreen` / `ChatScreen` /
 * `SettingsScreen`, which call `useCachedResource` themselves. An optional
 * argument on the hook is unreachable from those tests without threading a prop
 * through three screens purely for the suite. A context is injectable from
 * outside without production code knowing the seam exists.
 *
 * DEFAULTED, SO PRODUCTION MOUNTS NOTHING. `createContext(realScheduleRetry)`
 * means every consumer with no provider above it gets real timers — there is no
 * provider in any app entry, and adding one is not required for this to work.
 * The alternative (a module-level mutable + a `__setScheduleRetry` test hook)
 * would put a writable global in shipped code and make test order significant.
 *
 * The cost this buys back: the six screen-level retry tests waited 5 REAL
 * SECONDS each (~30s for `failedFetchIsNotEmpty.test.tsx` alone) because
 * `vi.useFakeTimers()` installed after the hook has already scheduled cannot
 * capture the pending timer — the handle lives in the real queue and swapping
 * the global does not migrate it. See the architect's web!312 review.
 */
export const ScheduleRetryContext = createContext(realScheduleRetry);
/**
 * Read a cached resource with stale-while-revalidate. Renders the cached value
 * immediately when warm (no loading flash on tab switch); shows `loading` only
 * when there is nothing cached yet.
 *
 * `error` EXISTS BECAUSE ITS ABSENCE WAS A FALSE STATEMENT. This hook used to
 * return `{ data, loading }` and swallow every rejection, so on a cold load a
 * FAILED fetch was byte-identical to a successful EMPTY one: `data === undefined,
 * loading === false`. Consumers could only read that as "no rows", and both agent
 * screens duly rendered «Пока нет ни одного агента» — telling the user something
 * about their account we had not established and that may well be false, with a
 * «Создать агента» CTA that fails on the same condition.
 *
 * The motivating case is the ORDINARY one: a transient 503, a network blip, a
 * 500 — any cold-load failure at all — makes the app assert the user has no
 * agents. It does NOT need an exotic trigger.
 *
 * Specifically NOT the justification, though it looks like the obvious one:
 * `IDENTITY_NOT_PROVISIONED` is already handled. `IdentityGapGate`
 * (`api/identityGap.ts`) latches on that reason off any transport call and
 * replaces the whole tree with the open-the-bot surface; web-ui and desktop both
 * mount it, and mobile cannot reach live OIDC at all yet (its shell throws). So
 * a freshly signed-up user does NOT land here — checked, 2026-08-10, after
 * asserting the opposite. What lands here is every OTHER failure, which is the
 * common case rather than the dramatic one.
 *
 * `error` and `data` are NOT exclusive, and that is the whole subtlety: a warm
 * cache whose revalidate fails keeps SHOWING the stale value (the reason this
 * cache exists) while also reporting the failure. Consumers decide — usually
 * "render the stale data, and say so only if there is nothing to render".
 *
 * `retrying` is the auto-retry window after a cold-load failure (no cached
 * value to fall back to). It is INTENTIONALLY FALSE on a warm-cache revalidate
 * failure — surfacing "we'll try again automatically" over data the user can
 * already read would be the opposite mistake (stale-while-revalidate exists
 * exactly so the user does not see a failure prompt when the data is fine).
 *
 * Auto-retry is a single attempt after `COLD_RETRY_MS`, not a loop. Cancellable
 * on unmount and on `reload()` — the effect cleanup clears the pending timer
 * before a new fetch runs, so a manual reload never races a stale auto-retry.
 */
export function useCachedResource(key, fetch) {
    // Host primitive, fixed for the app's lifetime (see ScheduleRetryContext).
    // Deliberately NOT in the effect's dep array below — same reasoning as `fetch`.
    const scheduleRetry = useContext(ScheduleRetryContext);
    const [snap, setSnap] = useState(() => {
        const cached = getCached(key);
        return { data: cached, loading: cached === undefined, error: undefined, retrying: false };
    });
    // Bumped by `reload` to re-run the effect. Paired with invalidateResource so
    // the retry actually hits the network instead of the still-fresh cache entry:
    // FRESH_MS short-circuits `loadResource` within 30s of a SUCCESS, and without
    // the invalidation the button would silently no-op — the dishonest affordance
    // web!297 removed, reintroduced.
    //
    // The invalidation is unreachable from today's UI (the reload button renders
    // only in the failed branch, and failures are never cached, so `loadResource`
    // refetches either way) — which is exactly why deleting it survived the whole
    // suite until it was pinned at the hook. See the `useCachedResource.reload`
    // test: it is the HOOK's contract that needs this, not any current screen.
    // (umnyi review of web!298, 2026-08-10.)
    const [attempt, setAttempt] = useState(0);
    const reload = useCallback(() => {
        invalidateResource(key);
        setSnap((s) => ({ data: s.data, loading: s.data === undefined, error: undefined, retrying: false }));
        setAttempt((n) => n + 1);
    }, [key]);
    useEffect(() => {
        let cancelled = false;
        let cancelRetry;
        // stale-while-revalidate: the initial state already reflects any warm cache
        // (no sync reset here — that would force an extra render). Just load + update
        // when the fetch settles.
        loadResource(key, fetch)
            .then((v) => {
            if (!cancelled)
                setSnap({ data: v, loading: false, error: undefined, retrying: false });
        })
            .catch((err) => {
            if (cancelled)
                return;
            // A warm cache whose revalidate fails: preserve stale-while-revalidate
            // (the reason this cache exists), report the failure, NO retry prompt —
            // surfacing "we'll try again automatically" over data the user can
            // already read is the opposite mistake.
            if (getCached(key) !== undefined) {
                setSnap((s) => ({ data: s.data, loading: false, error: err, retrying: false }));
                return;
            }
            // Cold-load failure: surface the error AND schedule a single auto-retry.
            // The hook returns `retrying: true` for `COLD_RETRY_MS` so the consumer
            // can render the forward-looking hint instead of an immediate dead-end
            // button. On retry success → snap normalises; on retry failure → snap
            // drops `retrying` and keeps the error for the manual-reload button.
            setSnap((s) => ({ data: s.data, loading: false, error: err, retrying: true }));
            cancelRetry = scheduleRetry(() => {
                if (cancelled)
                    return;
                loadResource(key, fetch)
                    .then((v) => {
                    if (!cancelled)
                        setSnap({ data: v, loading: false, error: undefined, retrying: false });
                })
                    .catch((retryErr) => {
                    if (!cancelled)
                        setSnap((s) => ({ data: s.data, loading: false, error: retryErr, retrying: false }));
                });
            }, COLD_RETRY_MS);
        });
        return () => {
            cancelled = true;
            cancelRetry?.();
        };
        // fetch is a fresh closure each render but semantically bound to `key`; the
        // client it closes over is stable (ApiProvider useMemo). Key on `key` alone,
        // plus `attempt` so an explicit reload re-runs it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, attempt]);
    // `retrying` and a non-undefined `data` are EXCLUSIVE by construction — the
    // cold-load branch (below) is gated on `getCached(key) === undefined`, so
    // `retrying: true` only ever holds when the cache has nothing to render. A
    // status enum would encode that state machine; a boolean pair documents it.
    // (architect, web!312 review 2026-08-12.)
    return { ...snap, reload };
}
// ── Resource definitions (single source for keys + fetchers; used by screens
//    AND the launch prefetch, so they can't drift). ──
export const AGENTS_ACTIVE_KEY = 'agents:active';
export const AGENTS_ALL_KEY = 'agents:all';
export const USAGE_KEY = 'usage';
export const fetchAgentsActive = (c) => () => c.agents.list().then((r) => r.agents ?? []);
export const fetchAgentsAll = (c) => () => c.agents.list({ includeInactive: true }).then((r) => r.agents ?? []);
/**
 * Carries `entitlement` alongside `usage` (both parts of the SAME `GetMyUsageResponse`, ADR-051
 * S7c D1 — same payload by construction so the two cannot skew). Previously discarded
 * `r.entitlement` entirely, back when nothing on the wire populated it
 * (`decisions/architect/2026-09-02-adr051-s7c-read-path-carrier-is-getmyusage-and-the-wire-is-
 * one-import.md`); `proto!341` (merged `dev` 2026-09-05) added the field.
 */
export const fetchUsage = (c) => () => c.usage.getMy().then((r) => ({ usage: r.usage ?? null, entitlement: r.entitlement ?? null }));
/** Invalidate both agent lists — call after creating/editing an agent. */
export function invalidateAgents() {
    invalidateResource(AGENTS_ACTIVE_KEY);
    invalidateResource(AGENTS_ALL_KEY);
}
export const ACCOUNT_CONTACT_KEY = 'account-contact';
/** Translates the 404-is-common-state wire behaviour into `AccountContactState` — a genuine
 *  error (any other status, or a network failure) still rejects and reaches `useCachedResource`'s
 *  `error` channel normally. */
export const fetchAccountContact = (c) => () => c.accountContact.get().then((r) => (r.display ? { kind: 'registered', display: r.display } : { kind: 'none' }), (err) => {
    if (isApiError(err) && err.httpStatus === 404)
        return { kind: 'none' };
    throw err;
});
// ── 025 S7 — durable goals web view ──────────────────────────────────────────
/** Per-agent (`getCurrent` is scoped to one caller+agent, D1: at most one non-terminal goal). */
export const currentGoalKey = (agentId) => `goal:current:${agentId}`;
/**
 * Discriminates `GOALS_NOT_PERMITTED` on the `(domain, reason)` tuple, `isGatewayReason`/
 * `isAgentCoreReason`-style — except this route's refusal is generated in tenant-service and
 * forwarded VERBATIM by the gateway (`GoalWebServer.passGoalStatus`), so it carries
 * `TENANT_ERROR_DOMAIN` (`tenant.agnivo.org`), not the gateway's own domain. Confirmed by reading
 * tenant-service's `statusWithReason` dispatch and gateway-service's passthrough on `dev`
 * 2026-09-27 — do not reach for `isGatewayReason` here, it is the wrong domain for this route.
 *
 * Only `GOALS_NOT_PERMITTED` gets its own state: it is a real upsell (D3, plan ceiling) and
 * showing it on a false positive would be a wrong claim, so it needs the strict tuple check.
 * `GOALS_NOT_PERMITTED` takes precedence over `GOALS_DISABLED` server-side (the plan ceiling is
 * the binding fact regardless of the per-agent enabled flag), so the server never emits both for
 * one request. Everything else in this 400 family — the real `GOALS_DISABLED` reason, a reason
 * this client doesn't recognise yet, or no ErrorInfo at all — collapses to `'disabled'` (renders
 * absent, ADR-175 ruling #5): that is the architect's own safety rail, "a client that does not
 * recognise a reason in this 400 family renders ABSENT, never a visible error," which lets web
 * and the tenant-service emitter ship in either order.
 */
export const fetchCurrentGoal = (c) => (agentId) => () => c.goals.getCurrent({ agentId }).then((r) => r.goal
    ? { kind: 'active', goal: r.goal, currentTermsRevision: r.currentTermsRevision, operator: r.operator }
    : { kind: 'none' }, (err) => {
    if (!isApiError(err))
        throw err;
    if (err.httpStatus === 404)
        return { kind: 'none' };
    if (isTenantReason(err, 'GOALS_NOT_PERMITTED'))
        return { kind: 'notPermitted' };
    if (err.httpStatus === 400)
        return { kind: 'disabled' };
    throw err;
});
/** Call after any mutation that can change the caller's current goal (accept/pause/resume/
 *  cancel/achieve/impossible/replace) — the discovery read has no push invalidation of its own. */
export function invalidateCurrentGoal(agentId) {
    invalidateResource(currentGoalKey(agentId));
}
/** The status view's checkpoint trail (`GoalPanel`'s expanded state) — one entry per goal. */
export const goalEventsKey = (goalId) => `goal:events:${goalId}`;
/**
 * `kinds: []` = every kind (the client-only `ListGoalEventsRequest.kinds` docblock) — the
 * expanded panel is a read-only trail, not a per-kind filter view, so it asks for everything.
 * `pageSize: 0, pageToken: ''` are the AIP-158 "unset" wire zero-values (the request docblock:
 * "the server caps an unset or oversized page") — unpaged for now: a goal's history is bounded
 * by its own lifetime and the panel is a first read-parity pass with `/goal status`; real
 * pagination (a load-more) is a follow-up once a goal old enough to need it is observed in
 * practice, not a speculative build. Until then, `truncated` still has to reach the render:
 * showing a capped first page under a heading that reads as the whole trail would present a
 * partial answer as complete (architect review of web!760).
 */
export const fetchGoalEvents = (c) => (goalId, agentId) => () => c.goals
    .listEvents({ goalId, agentId, kinds: [], pageSize: 0, pageToken: '' })
    .then((r) => ({ events: r.events ?? [], truncated: r.nextPageToken !== '' }));
//# sourceMappingURL=resourceCache.js.map